import { Icon } from '@iconify/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import RetailerAvatar from './RetailerAvatar';
import RetailerList from './RetailerList';
import RetailerTabsHeader from './RetailerTabsHeader';
import { PortalModal } from '@web/utils/src';
import { PRetailer } from '@/pages/FarmerPages/FarmersHomePage/FarmersHomePage';
import JoinRetailerModal from '@/pages/FarmerPages/FarmersHomePage/components/JoinRetailerModal';
import { useNearbyRetailersQuery } from '@/pages/FarmerPages/FarmersHomePage/nearbyRetailers.generated';
import { useReatailersQueryQuery } from '@/pages/FarmerPages/FarmersHomePage/retailers.generated';
import {
  getSelectedRetailerId,
  replaceRetailerInPath,
  setSelectedRetailerId,
} from '@/utils/retailerSelection';

type NonNullRetailer = Exclude<PRetailer, null>;

enum RetailerTabs {
  RETAILERS = 'retailers',
  NEARBY = 'nearby',
}

const ChoseRetailerButton: React.FC = () => {
  // state
  const [visible, setVisible] = useState(false);
  const [joinRetailerModalOpen, setJoinRetailerModalOpen] = useState(false);
  const [selectedJoinRetailer, setSelectedJoinRetailer] = useState<PRetailer>();
  const [currentTab, setCurrentTab] = useState<RetailerTabs>(
    RetailerTabs.RETAILERS,
  );

  // hooks
  const [{ data }] = useReatailersQueryQuery();
  const [{ data: nearbyRetailers }] = useNearbyRetailersQuery();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (data?.retailers?.length === 0) {
      setCurrentTab(RetailerTabs.NEARBY);
      setVisible(true);
    }
  }, [data?.retailers]);

  const retailers: NonNullRetailer[] = useMemo(
    () =>
      (data?.retailers ?? []).filter((r): r is NonNullRetailer => Boolean(r)),
    [data?.retailers],
  );
  const selectedRetailerId = getSelectedRetailerId();
  const selected: NonNullRetailer | null = useMemo(
    () => retailers.find((r) => r.id == selectedRetailerId) ?? null,
    [retailers, selectedRetailerId],
  );

  const handleSelect = useCallback(
    (retailerId: string) => {
      if (currentTab === RetailerTabs.RETAILERS) {
        const next = replaceRetailerInPath(location.pathname, retailerId);
        setSelectedRetailerId(retailerId);
        navigate(next);
        setVisible(false);
        return;
      }

      setSelectedJoinRetailer(
        nearbyRetailers?.retailers?.find((r) => r?.id === retailerId),
      );
      setVisible(false);
      setJoinRetailerModalOpen(true);
    },
    [currentTab, location.pathname, navigate, nearbyRetailers?.retailers],
  );

  const retailersToShow = useMemo(
    () =>
      currentTab === RetailerTabs.RETAILERS
        ? retailers
        : (nearbyRetailers?.retailers ?? []).filter((r): r is NonNullRetailer =>
            Boolean(r),
          ),
    [currentTab, nearbyRetailers?.retailers, retailers],
  );

  return (
    <div className="relative">
      <button
        type="button"
        className="cursor-pointer flex items-center gap-1-0 py-0-6 px-1-0 border-primary-yellow border-2 hover:bg-secondary-grey/60 rounded-8-0 transition-colors duration-200 ease-linear"
        onClick={() => setVisible(true)}
        data-testid="retailer_select_btn"
      >
        {selected ? (
          <>
            <RetailerAvatar
              id={selected.id}
              name={selected.name}
              sizeClass="w-4-0 h-4-0"
            />
            <span className="text-ellipsis max-w-[18rem] whitespace-nowrap overflow-hidden text-primary-black text-body1 font-md">
              {selected.name}
            </span>
          </>
        ) : (
          <span className="text-ellipsis max-w-[18rem] text-body1 whitespace-nowrap overflow-hidden text-primary-black">
            {t('retailer.select')}
          </span>
        )}
        <span aria-hidden className="rotate-90">
          <Icon icon="mdi:chevron-right" width={32} height={32} />
        </span>
      </button>

      <PortalModal
        isOpen={visible}
        setIsOpen={data?.retailers?.length === 0 ? undefined : setVisible}
      >
        <div className="w-full max-w-[420px] h-full bg-primary-white rounded-8-0 shadow">
          <p className="text-1-6 font-bd p-2-0">{t('retailer.select')}</p>
          <RetailerTabsHeader
            currentTab={
              currentTab === RetailerTabs.RETAILERS ? 'retailers' : 'nearby'
            }
            onChange={(tab) =>
              setCurrentTab(
                tab === 'retailers'
                  ? RetailerTabs.RETAILERS
                  : RetailerTabs.NEARBY,
              )
            }
            retailersTabHidden={data?.retailers?.length === 0}
            retailersLabel={t('retailer.retailers')}
            nearbyLabel={t('retailer.nearby_retailers')}
          />
          <RetailerList
            items={retailersToShow.map((r) => ({ id: r.id, name: r.name }))}
            selectedId={selected?.id ?? null}
            onSelect={handleSelect}
          />
        </div>
      </PortalModal>
      <JoinRetailerModal
        isOpen={joinRetailerModalOpen}
        setIsOpen={setJoinRetailerModalOpen}
        retailer={selectedJoinRetailer}
      />
    </div>
  );
};

export default ChoseRetailerButton;
